import {
  Box,
  Button,
  Divider,
  Grid,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CargoType, DocumentType, FreightService } from '@pelicargo/types';
import { confirmationOptions } from '@pelicargo/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HiChevronRight } from 'react-icons/hi';

import { DocumentsModal } from '../../components/Documents/DocumentsModal';
import { SimpleInput } from '../../components/SimpleInput';
import { SimpleQuantityInput } from '../../components/SimpleQuantityInput';
import { SimpleSwitch } from '../../components/SimpleSwitch';
import { trpc, TrpcOutputs } from '../../config/trpc';
import { usePersistItnNumber } from '../../hooks/shipmentForms/usePersistItnNumber';
import { AwbUploadCard } from './AwbUploadCard';
import { ShipmentInstructionsModal } from './ShipmentInstructionsModal';

type Props = {
  quote?: TrpcOutputs['getFinalQuote']['quote'];
  shipmentForms?: TrpcOutputs['getShipmentFormsByRequestId'];
  isOpen: boolean;
  onClose: () => void;
  aesRequested: boolean;
  hawbsRequired: boolean;
  /** Undefined when the booking form's question was left unanswered. */
  awbInstructionsReady?: boolean;
};

type QuoteBookedStep = 'summary' | 'shipment' | 'documents';
type DocumentsBackStep = Extract<QuoteBookedStep, 'summary' | 'shipment'>;

type YesNoRowProps = {
  label: string;
  name: 'aesRequested' | 'hawbsRequired' | 'awbInstructionsReady';
};

// Every card in the summary grid sits on this height, so the two columns line up
// row for row instead of each card sizing to its own content.
const CARD_HEIGHT = '88px';

const YesNoRow = ({ label, name }: YesNoRowProps) => (
  <Stack
    borderWidth="1px"
    borderColor="border.light"
    borderRadius="md"
    px="3"
    py="2.5"
    spacing="2"
    h="full"
    justifyContent="center"
  >
    <SimpleSwitch
      name={name}
      label={label}
      options={confirmationOptions}
      direction="row"
      variant="outline"
      fieldVariant="unstyled"
      size="sm"
    />
  </Stack>
);

export const QuoteBookedModal = ({
  quote,
  shipmentForms,
  isOpen,
  onClose,
  aesRequested: initialAes,
  hawbsRequired: initialHawbs,
  awbInstructionsReady: initialAwb,
}: Props) => {
  const methods = useForm<{
    aesRequested: boolean;
    hawbsRequired: boolean;
    awbInstructionsReady: boolean;
    hawbCount: number;
    itnNumber: string;
  }>({
    mode: 'onChange',
    defaultValues: {
      aesRequested: initialAes,
      hawbsRequired: initialHawbs,
      awbInstructionsReady: initialAwb,
      hawbCount: 1,
      itnNumber: '',
    },
  });

  const [currentStep, setCurrentStep] = useState<QuoteBookedStep>('summary');
  const [documentsBackStep, setDocumentsBackStep] = useState<DocumentsBackStep>('summary');
  const [shipmentMode, setShipmentMode] = useState<'update' | 'notify'>('update');

  const request = quote?.request;
  const requestId = request?.id;
  const trpcUtils = trpc.useUtils();

  const sliDocument = useMemo(
    () => request?.documents?.find((doc) => doc.type === DocumentType.SLI) ?? null,
    [request?.documents],
  );

  const handleSliChange = async () => {
    if (quote?.id) await trpcUtils.getFinalQuote.invalidate({ id: quote.id });
  };

  const persistItnNumber = usePersistItnNumber({ quoteId: quote?.id, requestId, shipmentForms });

  const aesRequested = methods.watch('aesRequested') ?? false;
  const hawbsRequired = methods.watch('hawbsRequired') ?? false;
  // Left tri-state on purpose: unanswered should behave like "not ready yet" and
  // continue into the AWB Instructions form, not skip it the way an explicit
  // "No" does.
  const awbInstructionsReady = methods.watch('awbInstructionsReady');
  const hawbCount = methods.watch('hawbCount') ?? 1;

  // The "Will there be HAWBs?" answer alone decides the layout: "Yes" opens the
  // consolidated MAWB + HAWB modal (using the chosen HAWB count), "No" opens the
  // direct MAWB modal.
  const numHawbs = Math.max(1, Number(hawbCount) || 1);
  const isDirectShipment = !hawbsRequired;
  const shipmentVariant = isDirectShipment ? 'direct' : 'mawb-hawb';
  const shipmentHawbCount = isDirectShipment ? 0 : numHawbs;

  // Seed the answers once per open. Keying on the values instead would re-run
  // mid-flow whenever the quote refetches (or the SLI upload invalidates it),
  // wiping whatever the user had already answered.
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      methods.reset({
        aesRequested: initialAes,
        hawbsRequired: initialHawbs,
        awbInstructionsReady: initialAwb,
        hawbCount: 1,
        itnNumber: '',
      });
      setCurrentStep('summary');
      setDocumentsBackStep('summary');
    }
    wasOpen.current = isOpen;
  }, [initialAes, initialAwb, initialHawbs, isOpen, methods]);

  const isFca = request?.freight_services?.includes(FreightService.FCA);
  const isDangerousGoods = request?.cargo_type === CargoType.DangerousGoods;

  const documentTypes = useMemo(() => {
    const suggested: DocumentType[] = [DocumentType.SLI, DocumentType.SHIPPER_DOCUMENTS, DocumentType.AIRWAY_BILL];
    if (isFca) suggested.push(DocumentType.BOL);
    if (isDangerousGoods) suggested.push(DocumentType.DGD, DocumentType.MSDS);
    return suggested;
  }, [isDangerousGoods, isFca]);

  const handleOpenShipmentForms = (mode: 'update' | 'notify') => () => {
    setShipmentMode(mode);
    setCurrentStep('shipment');
  };

  const handleBackToSummary = () => {
    setCurrentStep('summary');
  };

  const handleAdditionalDocumentsFromShipment = () => {
    setDocumentsBackStep('shipment');
    setCurrentStep('documents');
  };

  // "AWB instructions ready? No" skips the AWB Instructions modal, which is the only other
  // consumer of the ITN — so this path has to save it itself or the value is discarded.
  const handleAdditionalDocumentsFromSummary = async () => {
    await persistItnNumber(methods.getValues('itnNumber'));
    setDocumentsBackStep('summary');
    setCurrentStep('documents');
  };

  const handleDocumentsBack = () => {
    setCurrentStep(documentsBackStep);
  };

  const handleDocumentsSubmit = () => {
    setCurrentStep('summary');
    onClose();
  };

  return (
    <>
      <FormProvider {...methods}>
        <Modal
          isOpen={isOpen && currentStep === 'summary'}
          onClose={onClose}
          isCentered
          size={{ base: 'full', md: '2xl' }}
        >
          <ModalOverlay />
          <ModalContent borderRadius={{ base: '0', md: 'md' }}>
            <ModalCloseButton />
            <ModalHeader pb="2">
              <Heading variant="h4">Booking Requested!</Heading>
            </ModalHeader>
            <ModalBody>
              <Stack spacing="4">
                <Text color="text.subtle" fontSize="sm">
                  Please answer these questions, it will help speed up the process.
                </Text>
                <Divider />
                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(2, auto)' }}
                  autoRows={`minmax(${CARD_HEIGHT}, auto)`}
                  w={{ base: 'full', xl: 'fit-content' }}
                  mx="auto"
                  gap={{ base: '3', md: '2' }}
                  justifyContent="center"
                >
                  <YesNoRow label="Do you need AES Filing?" name="aesRequested" />

                  {/* AES: "Yes" reveals the SLI uploader, "No" reveals the ITN# field. */}
                  {aesRequested ? (
                    <AwbUploadCard
                      label="SLI"
                      documentType={DocumentType.SLI}
                      uploadHint="SLI (if available)"
                      showFormHint={false}
                      minH={CARD_HEIGHT}
                      requestId={requestId}
                      document={sliDocument}
                      onDocumentChange={handleSliChange}
                    />
                  ) : (
                    <Stack
                      borderWidth="1px"
                      borderColor="border.light"
                      borderRadius="md"
                      px="3"
                      py="2.5"
                      h="full"
                      justifyContent="center"
                      minW={0}
                    >
                      <SimpleInput
                        name="itnNumber"
                        label="ITN Number (if available)"
                        placeholder="XYYYYMMDD123456"
                        variant="unstyled"
                        inputProps={{ onBlur: (event) => void persistItnNumber(event.target.value) }}
                      />
                    </Stack>
                  )}

                  <YesNoRow label="Will there be HAWBs?" name="hawbsRequired" />

                  {/* HAWB: the count input only appears when "Yes" is selected. */}
                  {hawbsRequired ? (
                    <Box minW={0} h="full">
                      <SimpleQuantityInput name="hawbCount" label="How many?" min={1} simpleFieldProps={{ h: 'full' }} />
                    </Box>
                  ) : (
                    <Box minW={0} />
                  )}

                  <Box justifySelf="center" w="full">
                    <YesNoRow label="Are your AWB instructions ready?" name="awbInstructionsReady" />
                  </Box>
                </Grid>
              </Stack>
            </ModalBody>

            <ModalFooter>
              <Stack direction={{ base: 'column', sm: 'row' }} justify="flex-end" w="full" spacing="2">
                {/* Instructions ready -> fill them in now. An explicit "No" skips
                    ahead to documents; unanswered still continues. */}
                {awbInstructionsReady === false ? (
                  <Button
                    colorScheme="primary"
                    size="sm"
                    onClick={handleAdditionalDocumentsFromSummary}
                    rightIcon={<Icon as={HiChevronRight} />}
                  >
                    Additional Documents
                  </Button>
                ) : (
                  <Button
                    colorScheme="primary"
                    size="sm"
                    onClick={handleOpenShipmentForms('update')}
                    rightIcon={<Icon as={HiChevronRight} />}
                  >
                    Continue
                  </Button>
                )}
              </Stack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </FormProvider>
      <DocumentsModal
        isOpen={isOpen && currentStep === 'documents'}
        onClose={onClose}
        onBack={handleDocumentsBack}
        onSubmit={handleDocumentsSubmit}
        title="Additional Documents"
        submitLabel="Submit"
        backLabel="Back"
        mode="submit"
        documentTypes={documentTypes}
        defaultDocuments={request?.documents || []}
      />
      <ShipmentInstructionsModal
        quote={quote}
        isOpen={isOpen && currentStep === 'shipment'}
        flowIsOpen={isOpen}
        onClose={onClose}
        onBack={handleBackToSummary}
        onAdditionalDocuments={handleAdditionalDocumentsFromShipment}
        hawbCount={shipmentHawbCount}
        variant={shipmentVariant}
        mode={shipmentMode}
        initialItnNumber={methods.watch('itnNumber')}
      />
    </>
  );
};
